import { useEffect } from 'react';

import useMidiPlayer from './useMidiPlayer';
import usePreviewMix, { DEFAULT_CLICK_VOLUME, DEFAULT_MELODY_VOLUME, usePlayLabel } from './usePreviewMix';
import useSampleLoader from './useSampleLoader';
import useSetting from './useSetting';
import { settingKeys } from '../utils/settings';

/**
 * A part's MIDI, ready for ChordBarsTransport: the player plus the click, count-in and levels every
 * preview shares. The clip's level is the melody level the chart player uses for the same recording.
 *
 * @param {object} midiJson - The part's clip
 * @param {string} instrumentId - Preset to play it with
 * @param {boolean} isActive - Whether the modal holding it is open
 * @param {number} semitones - Shift to the key on screen
 * @param {number} bpmOverride - Practice tempo, or 0 for the clip's own
 */
const useMidiPreview = (midiJson, instrumentId, isActive = false, semitones = 0, bpmOverride = 0) => {
  const mix = usePreviewMix();
  const [melodyVolume, setMelodyVolume] = useSetting(settingKeys.melodyVolume, DEFAULT_MELODY_VOLUME);

  const player = useMidiPlayer(midiJson, instrumentId, isActive, semitones, bpmOverride, {
    metronome: mix.metronome,
    countIn: mix.countIn,
    metronomeVolume: mix.clickLevel,
    instrumentVolume: melodyVolume,
  });

  const { stop } = player;

  // Closing shouldn't leave the clip playing on behind whatever opened it
  useEffect(() => {
    if (!isActive) stop();
  }, [isActive, stop]);

  const samples = useSampleLoader(instrumentId, isActive);
  const { playLabel, isFetchingSamples, playBusy, playProgress } = usePlayLabel([{ samples, instrumentId }], player);

  // Reset in the mixer: the clip and the click back where they open the first time
  const canResetMix =
    Math.abs(melodyVolume - DEFAULT_MELODY_VOLUME) > 1e-6 ||
    Math.abs(mix.clickVolume - DEFAULT_CLICK_VOLUME) > 1e-6 ||
    !mix.metronome;
  const resetMix = () => {
    setMelodyVolume(DEFAULT_MELODY_VOLUME);
    mix.setClickVolume(DEFAULT_CLICK_VOLUME);
    mix.setMetronome(true);
  };

  return {
    ...player,
    samples,
    playLabel,
    playBusy,
    playProgress,
    canPlay: Boolean(midiJson) && !player.isLoading && !isFetchingSamples,
    metronome: mix.metronome,
    setMetronome: mix.setMetronome,
    countIn: mix.countIn,
    setCountIn: mix.setCountIn,
    clickVolume: mix.clickVolume,
    setClickVolume: mix.setClickVolume,
    instrumentVolume: melodyVolume,
    setInstrumentVolume: setMelodyVolume,
    canResetMix,
    resetMix,
  };
};

export default useMidiPreview;
